import { Card, Stack, ThemeIcon, Text } from "@mantine/core"
import { useMatchRoute, useNavigate, useParams } from "@tanstack/react-router"
import "./index.css"
import { PostAttachments } from "./PostAttachments"
import { PostHeader } from "./PostHeader"
import { PostReactionsAndComments } from "./PostReactionsAndComments"
import { useGetAuthenticatedUser } from "../../../../-components/use-authenticated-user"
import { DefaultError } from "../../../../../../-components/error-context"
import { sanitiseHTML } from "../../../../../../utils/sanitise-html"
import { IconPencil, IconTrash } from "@tabler/icons-react"
import { DottedMenu } from "../../../../../../-components/dotted-menu"
import { openConfirmModal } from "@mantine/modals"
import { addStyleToLinks } from "../../../../-components/TextTile/addStyleToLinks"
import { useTranslation } from "react-i18next"
import { EditFeedPostModal } from "./EditFeedPostModal"
import { useDisclosure } from "@mantine/hooks"
import {
  getChatGetFeedPostsForUserQueryKey,
  getChatGetFeedPostsQueryKey,
  useChatDeletePost,
  useGetSimpleUserSuspense,
} from "@/orval/gen"
import { ChatPostDto } from "@/orval/gen/model"
import { useChatGetChatByIdSuspense } from "@/orval/gen"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useQueryClient } from "@tanstack/react-query"

interface Props {
  feedPost: ChatPostDto
  hideComments?: boolean
}

export function FeedPostCard({ feedPost, hideComments }: Props) {
  const { t } = useTranslation("app_server")
  const { data: chat } = useChatGetChatByIdSuspense({
    chatId: feedPost.chatId,
  })

  const { data: author } = useGetSimpleUserSuspense({
    userId: feedPost.userId,
    includePerson: true,
  })

  const isOwnMessage = useGetAuthenticatedUser().data.userId === feedPost.userId

  const navigate = useNavigate()
  const postId = useParams({ strict: false }).postId
  const onCardClick = postId
    ? undefined
    : () =>
        navigate({
          to: "/app/com/feeds/$feedId/$postId",
          params: {
            feedId: feedPost.chatId.toString(),
            postId: feedPost.chatPostId.toString(),
          },
        })

  const matchesRoute = useMatchRoute()

  const queryClient = useQueryClient()

  const { mutateAsync: deletePost } = useChatDeletePost({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        if (matchesRoute({ to: "/app/com/feeds/$feedId/$postId" })) {
          navigate({
            to: "/app/com/feeds/$feedId",
            params: { feedId: feedPost.chatId.toString() },
          })
        }
        queryClient.invalidateQueries({
          queryKey: getChatGetFeedPostsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getChatGetFeedPostsForUserQueryKey(),
        })
      },
    },
  })

  if (!chat) throw new DefaultError({ title: "Chat not found." })

  const [opened, { open, close }] = useDisclosure()

  return (
    <Card
      w="100%"
      shadow="sm"
      maw={800}
      withBorder
      onClick={onCardClick}
      style={{ flexShrink: 0 }}
      className={onCardClick ? "topic-post-card" : undefined}
    >
      <Stack gap="xs">
        <PostHeader
          author={author}
          authorSubtext={`#${chat.name}`}
          timeStamp={feedPost.timeStamp}
          rightSection={
            isOwnMessage ? (
              <DottedMenu
                items={[
                  {
                    leftSection: (
                      <ThemeIcon size="xs" variant="subtle">
                        <IconPencil />
                      </ThemeIcon>
                    ),
                    children: t("Edit"),
                    onClick: (e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      open()
                    },
                  },
                  {
                    leftSection: (
                      <ThemeIcon size="xs" c="red" variant="subtle">
                        <IconTrash />
                      </ThemeIcon>
                    ),
                    children: t("Delete"),
                    onClick: (e) => {
                      e.stopPropagation()
                      e.preventDefault()
                      openConfirmModal({
                        title: t("DeleteEntity", { entity: "Post" }),
                        onConfirm: async () =>
                          await deletePost({
                            params: {
                              postId: feedPost.chatPostId,
                            },
                          }),
                        labels: {
                          cancel: t("Cancel"),
                          confirm: t("Delete"),
                        },
                      })
                    },
                  },
                ]}
              />
            ) : null
          }
        />
        <Text truncate size="lg" fw="bold">
          {feedPost.title}
        </Text>
        {feedPost.attachments.length > 0 && (
          <>
            <PostAttachments
              attachments={feedPost.attachments}
              chatPostId={feedPost.chatPostId}
            />
          </>
        )}
        <div
          style={{
            textOverflow: "ellipsis",
            overflow: "hidden",
            maxWidth: "100%",
            position: "relative",
          }}
          dangerouslySetInnerHTML={{
            __html: feedPost.postText
              ? sanitiseHTML(addStyleToLinks(feedPost.postText))
              : "",
          }}
        />
        <PostReactionsAndComments
          feedPost={feedPost}
          hideComments={hideComments}
        />
      </Stack>
      <EditFeedPostModal
        key={feedPost.chatPostId}
        opened={opened}
        chatPostDto={feedPost}
        onClose={close}
      />
    </Card>
  )
}
